import _ from "lodash";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import { getFormCompleteQuestions } from "./question";

// Direct copy of https://github.com/Avela-Education/apply-form-service/blob/main/src/resources/admin/form-templates/import.ts

const _ImportErrorSchema = z.discriminatedUnion("level", [
  z.object({
    level: z.literal("Question"),
    id: z.string().optional(),
    error: z.string(),
  }),
  z.object({
    level: z.literal("QuestionVisibility"),
    id: z.string().optional(),
    error: z.string(),
  }),
]);

export type ImportError = z.infer<typeof _ImportErrorSchema>;
export type ImportResult = {
  schema: AF.FormTemplate<AF.WithId>;
  errors: ImportError[];
};

export const ImportFromOtherPathParamsSchema = z.object({
  formTemplateId: z.string().uuid(),
  sourceFormTemplateId: z.string().uuid(),
});
export type ImportContext = Readonly<{
  questions: Readonly<{ [key: string]: string }>;
  options: Readonly<{
    [questionKey: string]: Readonly<{
      [value: string]: string;
    }>;
  }>;
  grades: Readonly<{
    [value: string]: string;
  }>;
  schools: Readonly<{
    [referenceId: string]: string;
  }>;
}>;

type Grade = { id: string; value: string | null };
type School = { id: string; name: string; reference_id: string | null };
export function getImportContext(
  schema: AF.FormTemplate<AF.WithId>,
  grades_: Grade[] = [],
  schools_: School[] = []
): ImportContext {
  const allQuestions = getFormCompleteQuestions(schema);

  // Allows mutation for when creating the context to get the most perf,
  // and return the Readonly version of the ImportContext.
  const questions: { [key: string]: string } = {};
  const options: { [questionKey: string]: { [value: string]: string } } = {};
  const grades: { [value: string]: string } = {};
  const schools: { [referenceId: string]: string } = {};

  for (const question of allQuestions) {
    if (!question.key) {
      continue;
    }
    questions[question.key] = question.id;

    const questionOptions: { [value: string]: string } = {};
    if ("options" in question) {
      for (const option of question.options) {
        if (!option.value) {
          continue;
        }

        questionOptions[option.value] = option.id;
      }
    }
    if (!_.isEmpty(questionOptions)) {
      options[question.key] = questionOptions;
    }
  }

  for (const grade of grades_) {
    if (grade.value) {
      grades[grade.value] = grade.id;
    }
  }

  for (const school of schools_) {
    if (school.reference_id) {
      schools[school.reference_id] = school.id;
    }
  }

  return {
    questions: questions,
    options: options,
    grades: grades,
    schools: schools,
  };
}

export function importSchema(
  schema: AF.FormTemplate<AF.WithId>,
  context: ImportContext
): [AF.FormTemplate<AF.WithId>, ImportError[]] {
  const errors: ImportError[] = [];
  const sections: AF.Section<AF.WithId>[] = [];

  for (const section of schema.sections) {
    const [importedSection, sectionErrors] = importSection(section, context);
    sections.push(importedSection);
    errors.push(...sectionErrors);
  }

  return [
    {
      ...schema,
      sections,
    },
    errors,
  ];
}

function importSection(
  section: AF.Section<AF.WithId>,
  context: ImportContext
): [AF.Section<AF.WithId>, ImportError[]] {
  const importedSection = { ...section };
  const errors: ImportError[] = [];

  if ("questions" in section && "questions" in importedSection) {
    const questions: AF.Question<AF.WithId>[] = [];

    for (const question of section.questions) {
      const [importedQuestion, questionErrors] = importQuestion(
        question,
        context
      );
      questions.push(importedQuestion);
      errors.push(...questionErrors);
    }

    importedSection.questions = questions;
  }

  return [importedSection, errors];
}

function importQuestion(
  question: AF.Question<AF.WithId>,
  context: ImportContext
): [AF.Question<AF.WithId>, ImportError[]] {
  const importedQuestion = { ...question };

  const errors: ImportError[] = [];
  if (question.visibility) {
    const [visibility, visibilityErrors] = importVisibility(
      question.visibility,
      { ...context, questionId: question.id }
    );

    importedQuestion.visibility = visibility;
    errors.push(...visibilityErrors);
  }

  if (importedQuestion.type === "Grades") {
    importedQuestion.options = []; // Empty out grades option since this is dynamically populated on fetch
  }

  return [importedQuestion, errors];
}

/**
 * Visibility
 */
type ImportVisibilityContext = ImportContext & Readonly<{ questionId: string }>;
export function importVisibility(
  visibility: AF.Visibility.Visibility,
  context: ImportVisibilityContext
): [AF.Visibility.Visibility, ImportError[]] {
  const importedVisibility: AF.Visibility.Visibility = { ...visibility };
  const errors: ImportError[] = [];
  if (visibility.allOf) {
    const [allOf, rulesErrors] = importRules(visibility.allOf, context);
    importedVisibility.allOf = allOf;
    errors.push(...rulesErrors);
  } else if (visibility.anyOf) {
    const [anyOf, rulesErrors] = importRules(visibility.anyOf, context);
    importedVisibility.anyOf = anyOf;
    errors.push(...rulesErrors);
  } else if (visibility.oneOf) {
    const [oneOf, rulesErrors] = importRules(visibility.oneOf, context);
    importedVisibility.oneOf = oneOf;
    errors.push(...rulesErrors);
  }

  return [importedVisibility, errors];
}

function importRules(
  rulesList: AF.Visibility.Rules[],
  context: ImportVisibilityContext
): [AF.Visibility.Rules[], ImportError[]] {
  const errors: ImportError[] = [];
  const importedRulesList: AF.Visibility.Rules[] = [];
  for (const { rules } of rulesList) {
    const importedRules: typeof rules = [];
    for (const rule of rules) {
      const [importedRule, ruleErrors] = importRule(rule, context);
      importedRules.push(importedRule);
      errors.push(...ruleErrors);
    }

    importedRulesList.push({ rules: importedRules });
  }

  return [importedRulesList, errors];
}

function importRule(
  rule: AF.Visibility.Rule,
  context: ImportVisibilityContext
): [AF.Visibility.Rule, ImportError[]] {
  const errors: ImportError[] = [];
  if (rule.dataType === "questionKey") {
    const [property, propertyErrors] = importRuleQuestionProperty(
      rule.property,
      context
    );
    const [value, valueErrors] = importRuleQuestionValue(
      rule.property,
      rule.value,
      context
    );
    const importedRule: AF.Visibility.Rule = {
      ...rule,
      property,
      value,
    };
    errors.push(...propertyErrors, ...valueErrors);
    return [importedRule, errors];
  } else if (rule.dataType === "school") {
    const [value, valueErrors] = importRuleSchoolValue(rule.value, context);
    const importedRule: AF.Visibility.Rule = {
      ...rule,
      value,
    };
    errors.push(...valueErrors);
    return [importedRule, errors];
  }

  return [rule, errors];
}

function importRuleQuestionProperty(
  property: string,
  context: ImportVisibilityContext
): [string, ImportError[]] {
  const errors: ImportError[] = [];

  const importedProperty = context.questions?.[property];

  if (!importedProperty) {
    errors.push({
      level: "QuestionVisibility",
      id: context.questionId,
      error: "Error importing property key",
    });
    return [property, errors];
  }

  return [importedProperty, errors];
}

function importRuleQuestionValue(
  property: string,
  value: string | string[],
  context: ImportVisibilityContext
): [string | string[], ImportError[]] {
  const errors: ImportError[] = [];
  if (Array.isArray(value)) {
    const importedValue: string[] = [];
    for (const key of value) {
      // `key` can either be `option.value`, `grade.value`, `option.id`, or `grade.id`. We will try to map `key` to UUID if possible, if not, just fall back to the original value.
      const importedItem =
        context.options?.[property]?.[key] ?? context.grades?.[key];
      if (!importedItem) {
        errors.push({
          level: "QuestionVisibility",
          id: context.questionId,
          error: `Error importing value: ${key}`,
        });
      }

      importedValue.push(importedItem ?? key);
    }
    return [importedValue, errors];
  } else {
    // `key` can either be `option.value`, `grade.value`, `option.id`, or `grade.id`. We will try to map `key` to UUID if possible, if not, just fall back to the original value.
    const importedValue =
      context.options?.[property]?.[value] ?? context.grades?.[value];
    if (!importedValue) {
      errors.push({
        level: "QuestionVisibility",
        error: `Error importing value: ${value}`,
      });
    }

    return [importedValue ?? value, errors];
  }
}

function importRuleSchoolValue(
  value: string | string[],
  context: ImportVisibilityContext
): [string | string[], ImportError[]] {
  const errors: ImportError[] = [];
  if (Array.isArray(value)) {
    const importedValue: string[] = [];
    for (const referenceId of value) {
      const importedItem = context.schools?.[referenceId];
      if (!importedItem) {
        errors.push({
          level: "QuestionVisibility",
          id: context.questionId,
          error: `Error importing school reference_id: ${referenceId}`,
        });
      }

      importedValue.push(importedItem ?? referenceId);
    }
    return [importedValue, errors];
  } else {
    const importedValue = context.schools?.[value];
    if (!importedValue) {
      errors.push({
        level: "QuestionVisibility",
        id: context.questionId,
        error: `Error importing school reference_id: ${value}`,
      });
    }

    return [importedValue ?? value, errors];
  }
}

export function generateId(
  formTemplate: AF.FormTemplate<AF.WithoutId>
): AF.FormTemplate<AF.WithId> {
  const sections = formTemplate.sections.map(generateSectionId);
  return {
    ...formTemplate,
    sections,
    id: crypto.randomUUID(),
  } as AF.FormTemplate<AF.WithId>;
}

export function generateSectionId(
  section: AF.Section<AF.WithoutId>
): AF.Section<AF.WithId> {
  const questions =
    "questions" in section
      ? section.questions.map(generateQuestionId)
      : undefined;
  return {
    ...section,
    ...(questions ? { questions } : undefined),
    id: crypto.randomUUID(),
  } as AF.Section<AF.WithId>;
}

export function generateQuestionId(
  question: AF.Question<AF.WithoutId>
): AF.Question<AF.WithId> {
  const options =
    "options" in question ? question.options.map(generateOptionId) : undefined;
  return {
    ...question,
    ...(options ? { options } : undefined),
    id: crypto.randomUUID() as string,
  } as AF.Question<AF.WithId>;
}

export function generateOptionId(
  option: AF.Option<AF.WithoutId>
): AF.Option<AF.WithId> {
  return {
    ...option,
    id: crypto.randomUUID(),
  } as AF.Option<AF.WithId>;
}
